/**
 * ForgotPasswordForm — autenticazione Phase 6 (IMPL-P6-001).
 *
 * UC-008 step 1. Single email field. `react-hook-form` + `zodResolver(forgotPasswordSchema)`
 * from `@app/shared`. Submits to `/api/auth/custom/forgot-password` via
 * `authClient.forgotPassword()`.
 *
 * Anti-enumeration: regardless of API response (success, 4xx, network error)
 * we ALWAYS navigate to `/password-dimenticata/conferma` so the user cannot
 * differentiate between an existing account and a missing one.
 */
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { forgotPasswordSchema, type ForgotPasswordInput } from '@app/shared';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { authClient } from '../lib/auth-client';

export function ForgotPasswordForm() {
  const navigate = useNavigate();

  const form = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onTouched',
    defaultValues: {
      email: '',
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = form;

  async function onSubmit(values: ForgotPasswordInput) {
    // Fire-and-forget: regardless of API outcome, we always navigate to the
    // neutral confirm page (anti-enumeration — UC-008 BR).
    try {
      await authClient.forgotPassword(values);
    } catch {
      /* swallow — neutral UX */
    } finally {
      navigate('/password-dimenticata/conferma', { replace: true });
    }
  }

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="forgot-email">Email</Label>
        <Input
          id="forgot-email"
          type="email"
          autoComplete="email"
          inputMode="email"
          placeholder="nome@esempio.it"
          aria-invalid={!!errors.email || undefined}
          aria-describedby={errors.email ? 'forgot-email-error' : undefined}
          {...register('email')}
        />
        {errors.email ? (
          <p id="forgot-email-error" role="alert" className="text-sm font-medium text-destructive">
            {errors.email.message}
          </p>
        ) : null}
      </div>

      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? 'Invio in corso…' : 'Invia link di reimpostazione'}
      </Button>
    </form>
  );
}
